import React, { useState, useCallback, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { AudioLines, Loader2, RefreshCw, Link2, Unlink, Download, Check } from 'lucide-react';

export function PlaudSettingsDialog({ open, onOpenChange, onChanged }) {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [recordings, setRecordings] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [importingId, setImportingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const s = await api.getPlaudSettings();
      setSettings(s.data);
      if (s.data.connected) loadRecordings();
    } catch { toast.error('Failed to load Plaud settings'); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [recsError, setRecsError] = useState('');
  const [diag, setDiag] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);

  const loadRecordings = async () => {
    setLoadingRecs(true);
    setRecsError('');
    try {
      const r = await api.getPlaudRecordings(14);
      setRecordings(r.data);
    } catch (err) {
      setRecordings([]);
      setRecsError(err.response?.data?.detail || `Fehler ${err.response?.status || ''}`);
      if (err.response?.status !== 409) toast.error(err.response?.data?.detail || 'Failed to load recordings');
    }
    setLoadingRecs(false);
  };

  const runDiagnose = async () => {
    setDiagnosing(true);
    try { const r = await api.plaudDiagnose(); setDiag(r.data); }
    catch (err) { setDiag({ error: err.response?.data?.detail || String(err) }); }
    setDiagnosing(false);
  };

  useEffect(() => { if (open) { setRecordings(null); load(); } }, [open, load]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const r = await api.plaudConnect();
      window.location.href = r.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start Plaud connection');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.plaudDisconnect();
      toast.success('Plaud disconnected');
      load();
    } catch { toast.error('Failed to disconnect'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.savePlaudSettings({ enabled: settings.enabled, name_keyword: settings.name_keyword });
      toast.success('Plaud settings saved');
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      if (isAdmin) {
        await api.savePlaudSettings({ enabled: settings.enabled, name_keyword: settings.name_keyword });
      }
      const res = await api.checkPlaudInbox();
      if (res.data.error === 'not_connected') {
        toast.error('Plaud is not connected');
      } else if (res.data.error) {
        toast.error('Plaud query failed — try again');
      } else if (res.data.pending) {
        toast.info('Check is running in the background — reload the Team Meeting page in a moment, the banner will appear once the import is done');
      } else {
        toast.success(res.data.found
          ? `${res.data.found} new recording${res.data.found === 1 ? '' : 's'} imported`
          : 'No new matching recordings found');
        if (res.data.found) { onChanged?.(); loadRecordings(); }
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Check failed'); }
    setChecking(false);
  };

  const handleImport = async (rec) => {
    setImportingId(rec.id);
    try {
      await api.importPlaudRecording(rec.id, rec.name, rec.created_at);
      toast.success(`"${rec.name}" imported — ready for review on the meeting page`);
      setRecordings(prev => prev.map(r => r.id === rec.id ? { ...r, imported: true } : r));
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    setImportingId(null);
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[85vh] flex flex-col" data-testid="plaud-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <AudioLines className="w-5 h-5 text-violet-400" /> Plaud Connection
          </DialogTitle>
        </DialogHeader>
        {!settings ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 mt-1 pr-1">
            {/* Connection status */}
            <div className="border border-border rounded-sm p-3 bg-secondary/30 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-foreground font-medium" data-testid="plaud-connection-status">
                  {settings.connected
                    ? (settings.needs_reconnect ? '⚠ Connection expired' : '✓ Your Plaud account is connected')
                    : 'Your Plaud account is not connected'}
                </p>
                {settings.connected && settings.connected_at && !settings.needs_reconnect && (
                  <p className="text-[10px] text-muted-foreground">since {fmtDate(settings.connected_at)}</p>
                )}
              </div>
              {settings.connected && !settings.needs_reconnect ? (
                <Button variant="outline" onClick={handleDisconnect} data-testid="plaud-disconnect-btn"
                  className="border-red-600/40 text-red-400 hover:bg-red-600/10 rounded-sm text-xs h-8 px-3">
                  <Unlink className="w-3.5 h-3.5 mr-1.5" /> Disconnect
                </Button>
              ) : (
                <Button onClick={handleConnect} disabled={connecting} data-testid="plaud-connect-btn"
                  className="bg-violet-600 hover:bg-violet-500 text-white rounded-sm text-xs h-8 px-3">
                  {connecting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5 mr-1.5" />}
                  {settings.needs_reconnect ? 'Reconnect Plaud' : 'Connect Plaud'}
                </Button>
              )}
            </div>

            {/* Auto-import settings (admin-managed, global) */}
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <span className="text-sm text-foreground">Auto-import new recordings (checked every 5 min)</span>
              <Switch checked={!!settings.enabled} disabled={!isAdmin} data-testid="plaud-settings-enabled"
                onCheckedChange={(v) => setSettings(s => ({ ...s, enabled: v }))} />
            </label>
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Recording name keyword</p>
              <Input value={settings.name_keyword || ''} disabled={!isAdmin} data-testid="plaud-settings-keyword"
                onChange={(e) => setSettings(s => ({ ...s, name_keyword: e.target.value }))}
                placeholder="team meeting"
                className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground" />
              <p className="text-[10px] text-muted-foreground mt-1">
                Only recordings whose name contains this keyword are auto-imported (case-insensitive —
                "OLTA Nachfolge Team Meeting" matches "team meeting"). Any other recording can be imported manually below.
                {!isAdmin && ' Auto-import & keyword are managed by the admin.'}
              </p>
            </div>

            {/* Browse recent recordings */}
            {settings.connected && !settings.needs_reconnect && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Recent recordings (14 days)</p>
                  <button onClick={loadRecordings} disabled={loadingRecs} data-testid="plaud-recordings-refresh"
                    className="text-violet-400 hover:text-violet-300 p-1">
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingRecs ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                {loadingRecs && !recordings ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                ) : (recordings || []).length === 0 ? (
                  <div>
                    <p className="text-xs text-muted-foreground italic">No recordings found in the last 14 days.{recsError ? ` (${recsError})` : ''}</p>
                    <button onClick={runDiagnose} disabled={diagnosing} data-testid="plaud-diagnose-btn"
                      className="mt-1.5 text-[11px] text-violet-400 hover:text-violet-300 inline-flex items-center gap-1">
                      {diagnosing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Diagnose anzeigen
                    </button>
                  </div>
                ) : (
                  <div className="border border-border rounded-sm divide-y divide-border max-h-56 overflow-y-auto" data-testid="plaud-recordings-list">
                    {recordings.map(rec => (
                      <div key={rec.id} className="flex items-center gap-3 px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-foreground truncate">{rec.name}</p>
                          <p className="text-[10px] text-muted-foreground">{fmtDate(rec.created_at)}{rec.duration_min ? ` · ${rec.duration_min} min` : ''}</p>
                        </div>
                        {rec.imported ? (
                          <span className="text-[10px] text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> imported</span>
                        ) : (
                          <Button onClick={() => handleImport(rec)} disabled={importingId === rec.id}
                            data-testid={`plaud-recording-import-${rec.id}`}
                            variant="outline" className="border-violet-600/40 text-violet-400 hover:bg-violet-600/10 rounded-sm text-[10px] h-6 px-2">
                            {importingId === rec.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Download className="w-3 h-3 mr-1" /> Import</>}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {diag && (
              <div className="border border-violet-500/30 bg-violet-500/5 rounded-sm p-2" data-testid="plaud-diagnose-result">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] text-violet-300 uppercase tracking-wider">Plaud-Diagnose (bitte Screenshot an den Bug-Report)</p>
                  <button onClick={() => setDiag(null)} className="text-[10px] text-muted-foreground hover:text-foreground">schließen</button>
                </div>
                <pre className="text-[10px] leading-snug text-foreground/80 whitespace-pre-wrap break-all max-h-64 overflow-y-auto font-mono">{JSON.stringify(diag, null, 1)}</pre>
              </div>
            )}

            <p className="text-[10px] text-muted-foreground/60">
              Privacy: each team member connects their own Plaud account — nobody can see anyone else's recordings
              or imports. Reviewed &amp; applied notes become part of the shared meeting. Beta feature — data is
              retrieved via Plaud's US-hosted access server (processed in transit only, not stored there).
            </p>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-border sticky bottom-0 bg-card">
              <Button variant="outline" onClick={handleCheckNow} disabled={checking || !settings.connected}
                data-testid="plaud-settings-check-now"
                className="border-violet-600/40 text-violet-400 hover:bg-violet-600/10 rounded-sm text-xs h-8 px-3">
                {checking ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                Check now
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground rounded-sm text-xs h-8 px-3">
                  {isAdmin ? 'Cancel' : 'Close'}
                </Button>
                {isAdmin && (
                  <Button onClick={handleSave} disabled={saving} data-testid="plaud-settings-save"
                    className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-4">
                    {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Save
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
